import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { Button, Snackbar } from '../../../ui/atoms';
import Banner from '../../../components/Notifications/Banner';

const PreviewBrowserChrome = ({ title, children }) => (
  <div className="rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm">
    <div className="h-8 bg-gray-50 border-b border-gray-200 px-3 flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="text-[11px] text-gray-500 ml-2">{title}</span>
    </div>
    {children}
  </div>
);

const AskAiStaticPreview = ({ primaryColor }) => {
  const { t } = useTranslation();

  return (
    <PreviewBrowserChrome title={t('widgets.config.installation.preview.livePreview', { defaultValue: 'Live preview' })}>
      <div className="p-4 space-y-4 bg-white" style={{ minHeight: '200px' }}>
        <div className="space-y-2 max-w-prose">
          <h4 className="text-sm font-semibold text-gray-900 leading-snug">
            {t('widgets.config.installation.preview.sampleTitle')}
          </h4>
          <p className="text-xs text-gray-600 leading-relaxed">
            {t('widgets.config.installation.preview.sampleParagraph1')}
          </p>
          <p className="text-xs text-gray-500 leading-relaxed">
            {t('widgets.config.installation.preview.sampleParagraph2')}
          </p>
        </div>
        <button
          type="button"
          disabled
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white shadow-sm"
          style={{ backgroundColor: primaryColor || '#63AD61' }}
        >
          <SparklesIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('widgets.config.installation.preview.askAiButton')}
        </button>
      </div>
    </PreviewBrowserChrome>
  );
};

const buildPreviewPageHtml = (mode) => {
  const article = `
    <article style="padding:16px 16px 8px;font-family:system-ui,-apple-system,sans-serif;color:#374151;max-width:640px;">
      <h1 style="margin:0 0 8px;font-size:15px;font-weight:600;color:#111827;line-height:1.35;">
        How AI can support your content workflow
      </h1>
      <p style="margin:0 0 8px;font-size:13px;line-height:1.55;color:#4b5563;">
        ProseCaptain helps teams draft, refine, and publish content faster - with your brand voice built in.
      </p>
      <p style="margin:0 0 12px;font-size:13px;line-height:1.55;color:#6b7280;">
        Readers can ask follow-up questions without leaving the page.
      </p>
    </article>`;

  return mode === 'faq' ? `${article}` : '';
};

const buildIframeSrcDoc = (baseUrl, widgetId, widgetToken, mode) => {
  if (!widgetId || !widgetToken) return '';
  const modeAttribute = mode ? ` data-widget-mode="${mode}"` : '';
  const pageContent = buildPreviewPageHtml(mode);
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { margin: 0; padding: 0; width: 100%; min-height: 100%; background: #ffffff; }
    </style>
  </head>
  <body>
    ${pageContent}
    <script
      src="${baseUrl}/embed.js"
      data-widget-token="${widgetToken}"
      data-widget-id="${widgetId}"
      data-theme="light"
      data-widget-server-url="${baseUrl}"${modeAttribute}
    ></script>
  </body>
</html>`;
};

const LiveWidgetEmbedPreview = ({ baseUrl, widgetId, widgetToken, mode, primaryColor }) => {
  const { t } = useTranslation();
  const previewType = mode || 'chat';
  const iframeSrcDoc = buildIframeSrcDoc(baseUrl, widgetId, widgetToken, mode);

  if (previewType === 'ask') {
    return (
      <div className="relative rounded-lg border border-gray-200 bg-gray-50 p-2">
        <AskAiStaticPreview primaryColor={primaryColor} />
      </div>
    );
  }

  const iframeHeight = previewType === 'chat' ? 420 : 280;

  return (
    <div className="relative rounded-lg border border-gray-200 bg-gray-50 p-2">
      {!widgetId || !widgetToken ? (
        <p className="text-xs text-gray-400 p-2">
          {t('widgets.config.installation.preview.missingCredentials', {
            defaultValue: 'Missing widget id or widget token.'
          })}
        </p>
      ) : (
        <PreviewBrowserChrome title={t('widgets.config.installation.preview.livePreview', { defaultValue: 'Live preview' })}>
          <iframe
            title={`widget-preview-${previewType}`}
            className="block w-full bg-white"
            style={{ height: `${iframeHeight}px`, border: '0' }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            srcDoc={iframeSrcDoc}
          />
        </PreviewBrowserChrome>
      )}
    </div>
  );
};

const WidgetInstallation = ({ widgetConfig, widgetToken }) => {
  const { t } = useTranslation();
  const WIDGET_BASE_URL = 'https://widget-api.prosecaptain.com';
  const [snackbarState, setSnackbarState] = useState(null);

  const baseScriptAttrs = `data-widget-token="${widgetToken}"
  data-widget-id="${widgetConfig.id}"
  data-theme="light"
  data-widget-server-url="${WIDGET_BASE_URL}"`;

  const previewOptions = [
    {
      id: 'chat',
      title: t('widgets.config.installation.embed.options.chat.title', { defaultValue: 'Chat Widget' }),
      description: t('widgets.config.installation.embed.options.chat.description', {
        defaultValue: 'Floating chat bubble with full conversation window.'
      }),
      mode: null,
      code: `<script
  src="${WIDGET_BASE_URL}/embed.js"
  ${baseScriptAttrs}
></script>`
    },
    {
      id: 'faq',
      title: t('widgets.config.installation.embed.options.faq.title', { defaultValue: 'FAQ Widget' }),
      description: t('widgets.config.installation.embed.options.faq.description', {
        defaultValue: 'Inline FAQ chips view that opens chat on question click.'
      }),
      mode: 'faq',
      code: `<script
  src="${WIDGET_BASE_URL}/embed.js"
  ${baseScriptAttrs}
  data-widget-mode="faq"
></script>`
    },
    {
      id: 'ask',
      title: t('widgets.config.installation.embed.options.ask.title', { defaultValue: 'Ask AI Widget' }),
      description: t('widgets.config.installation.embed.options.ask.description', {
        defaultValue: 'Single inline CTA button that opens the assistant.'
      }),
      mode: 'ask',
      code: `<script
  src="${WIDGET_BASE_URL}/embed.js"
  ${baseScriptAttrs}
  data-widget-mode="ask"
></script>`
    }
  ];

  const apiIntegration = {
    title: t('widgets.config.installation.apiIntegration.title', { defaultValue: 'API Integration' }),
    description: t('widgets.config.installation.apiIntegration.description', {
      defaultValue: 'Backend/frontend direct integration via API endpoints.'
    })
  };

  const handleCopy = async (code) => {
    const showCopied = () => {
      setSnackbarState({
        type: 'success',
        message: t('widgets.config.installation.messages.copied', { defaultValue: 'Copied to clipboard' })
      });
    };

    const showError = () => {
      setSnackbarState({
        type: 'error',
        message: t('widgets.config.installation.messages.copyError', { defaultValue: 'Failed to copy' })
      });
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        showCopied();
        return;
      }

      // Fallback for environments where Clipboard API is unavailable.
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        showCopied();
      } else {
        showError();
      }
    } catch (error) {
      showError();
    }
  };

  return (
    <div className="space-y-8 mb-8">
      {/* Widget Identification */}
      {widgetConfig.id && (
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <h3 className="text-xl font-medium text-gray-900 mb-4">{t('widgets.config.installation.embed.title')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('widgets.config.installation.embed.optionsDescription', {
              defaultValue: 'Embed options with side-by-side code and live preview.'
            })}
          </p>

          <div className="space-y-5">
            {previewOptions.map((option) => (
              <div key={option.id} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-2">
                      <p className="text-sm font-semibold text-gray-900">{option.title}</p>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200 font-mono text-sm overflow-x-auto whitespace-pre-wrap">
                      {option.code}
                    </div>

                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      shape="square"
                      className="mt-2"
                      onClick={() => handleCopy(option.code)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                      {t('widgets.config.installation.embed.copyButton')}
                    </Button>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      {t('widgets.config.installation.preview.title', { defaultValue: 'Preview' })}
                    </p>
                    <LiveWidgetEmbedPreview
                      baseUrl={WIDGET_BASE_URL}
                      widgetId={widgetConfig.id}
                      widgetToken={widgetToken}
                      mode={option.mode}
                      primaryColor={widgetConfig.widget_color}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      {option.id === 'ask'
                        ? t('widgets.config.installation.preview.exampleLayout')
                        : t('widgets.config.installation.preview.loadedFromCode')}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="mb-2">
                <p className="text-sm font-semibold text-gray-900">{apiIntegration.title}</p>
                <p className="text-sm text-gray-500">{apiIntegration.description}</p>
              </div>

              <Banner
                title={t('widgets.config.installation.apiIntegration.banner.title', {
                  defaultValue: 'Need advanced API setup?'
                })}
                description={t('widgets.config.installation.apiIntegration.banner.description', {
                  defaultValue: 'Check full API docs and integration examples, or contact us and we will help with implementation.'
                })}
                actions={
                  <>
                    <Button
                      variant="primary-outline"
                      size="sm"
                      shape="rounded"
                      onClick={() => window.open('https://prosecaptain.com/dokumentacja/', '_blank', 'noopener,noreferrer')}
                    >
                      {t('widgets.config.installation.apiIntegration.banner.openDocs', {
                        defaultValue: 'Open Documentation'
                      })}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      shape="rounded"
                      onClick={() => window.open('https://prosecaptain.com/kontakt', '_blank', 'noopener,noreferrer')}
                    >
                      {t('widgets.config.installation.apiIntegration.banner.contactUs', {
                        defaultValue: 'Contact us'
                      })}
                    </Button>
                  </>
                }
              />
            </div>

            <div className="text-sm text-gray-500">
              {t('widgets.config.installation.embed.description')}
            </div>
          </div>
        </div>
      )}
      {snackbarState && (
        <Snackbar
          message={snackbarState.message}
          type={snackbarState.type}
          actionName={t('common.close', { defaultValue: 'Close' })}
          onClose={() => setSnackbarState(null)}
        />
      )}
    </div>
  );
};

export default WidgetInstallation;

